import { type Ref } from "vue";
import type {
  CustomHighlightItem,
  HighlightItem,
} from "@/features/experience-shared/highlights-v2/types/highlight-item";
import { uuid } from "@/utils/uuid";
import { replaceSlice } from "@/features/experience-shared/highlights-v2/composables/highlight-slice";
import { useIndexedList } from "@/features/experience-shared/highlights-v2/composables/useIndexedList";

/**
 * Headless, store-free engine for the CUSTOM slice of the highlights list.
 *
 * Custom highlights are free text: added, renamed, reordered and removed one at a time. The
 * generic slice mechanics (remove / move / reorder over the shared `HighlightItem[]`) come
 * from `useIndexedList`; this composable adds only the custom-specific bits: single-item add
 * (returning the new id) and in-place rename.
 *
 * Like `usePremadeList`, it is free of UI concerns: no `readonly`, no edit
 * lock, no draft/editing session, no validation. Editing (which row has an open textarea, the
 * draft text, trim/whitespace rejection) lives in the components. `addCustom` / `renameCustom`
 * take an already-committed name — the editor component trims and rejects blanks first.
 */
export function useCustomList(items: Ref<HighlightItem[]>) {
  const {
    items: customItems,
    remove,
    move,
    reorder,
  } = useIndexedList<HighlightItem, CustomHighlightItem>(
    items,
    (parent) => parent.filter((item): item is CustomHighlightItem => item.kind === "custom"),
    (next) => replaceSlice(items.value, "custom", next)
  );

  /**
   * Appends a new custom item with the given (already-trimmed) name and returns its id.
   * Validation is the caller's concern — this assumes a non-empty name.
   */
  function addCustom(name: string): string {
    // Match the existing RAW creation flow (`NewHighlight.vue`): a new custom highlight gets
    // both a client `id` and a SEPARATE internal `code`, each a distinct `uuid()`. The code is
    // preserved across adapter round-trips and edits so the RAW save path keeps it.
    const newItem: CustomHighlightItem = { kind: "custom", id: uuid(), name, code: uuid() };
    reorder([...customItems.value, newItem]);
    return newItem.id;
  }

  /** Renames an existing custom item in place. No-op for an unknown id. */
  function renameCustom(id: string, name: string): void {
    items.value = items.value.map((item) => (item.id === id && item.kind === "custom" ? { ...item, name } : item));
  }

  return {
    customItems,
    addCustom,
    renameCustom,
    reorderCustom: reorder,
    removeCustom: remove,
    moveCustom: move,
  };
}
